import { useEffect } from 'react';
import { create } from 'zustand';

import { type WhatsAppContactId } from '@/config/whatsappContacts';

type PageContextState = {
  /** Set by pages whose WhatsApp message depends on loaded data. */
  whatsappMessage: string | null;
  setWhatsAppMessage: (message: string | null) => void;
  /** Set by pages whose WhatsApp *number* depends on loaded data. */
  whatsappContactId: WhatsAppContactId | null;
  setWhatsAppContactId: (contactId: WhatsAppContactId | null) => void;
};

const usePageContextStore = create<PageContextState>()((set) => ({
  whatsappMessage: null,
  setWhatsAppMessage: (whatsappMessage) => set({ whatsappMessage }),
  whatsappContactId: null,
  setWhatsAppContactId: (whatsappContactId) => set({ whatsappContactId }),
}));

export const usePageWhatsAppOverride = (): string | null =>
  usePageContextStore((s) => s.whatsappMessage);

export const usePageWhatsAppContactOverride = (): WhatsAppContactId | null =>
  usePageContextStore((s) => s.whatsappContactId);

/**
 * Overrides the WhatsApp message for as long as this page is open — used where
 * the message names the car being viewed. Cleared on the way out, so the next
 * page never inherits it.
 */
export function useSetPageWhatsAppMessage(message: string | null): void {
  const set = usePageContextStore((s) => s.setWhatsAppMessage);
  useEffect(() => {
    set(message);
    return () => set(null);
  }, [message, set]);
}

/**
 * Overrides which seat answers for as long as this page is open, for a page
 * that cannot know its own number from its path — an accessory routed by the
 * category it turns out to be in, say, where the path is only `/accessories/:slug`.
 *
 * Nothing calls it yet: routing is page-level and the route map in
 * `config/whatsappContacts.ts` covers every page as it stands. It is here as
 * the matching half of `useSetPageWhatsAppMessage`, so routing a page from
 * loaded data later is one line in that page rather than a new mechanism.
 *
 * Cleared on unmount, for the same reason the message override is: a number
 * left behind would answer for whatever page came next.
 */
export function useSetPageWhatsAppContact(contactId: WhatsAppContactId | null): void {
  const set = usePageContextStore((s) => s.setWhatsAppContactId);
  useEffect(() => {
    set(contactId);
    return () => set(null);
  }, [contactId, set]);
}
